/**
 * UbiquitousLanguage - The shared vocabulary within a Bounded Context.
 *
 * In Domain-Driven Design, the Ubiquitous Language is the shared terminology
 * used by all team members within a Bounded Context. It bridges the gap between
 * domain experts and developers.
 *
 * This implementation represents the Ubiquitous Language as a collection of
 * VocabularyBindings, each identifying a specific SKOS ConceptScheme via
 * vocabularyIri. The binding layer handles storage concerns (where the data
 * lives), while the UL specifies which ConceptSchemes are adopted.
 */
/**
 * Default implementation of UbiquitousLanguage.
 */
export class UbiquitousLanguageImpl {
    constructor(params) {
        Object.defineProperty(this, "id", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "name", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "_vocabularyBindings", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: []
        });
        this.id = params.id;
        this.name = params.name;
    }
    getBindingUri(binding) {
        return binding.vocabularyIri ?? binding.namespace ?? '';
    }
    getVocabularyBindings() {
        return [...this._vocabularyBindings];
    }
    addVocabularyBinding(binding) {
        const uri = this.getBindingUri(binding);
        if (!this._vocabularyBindings.find(b => this.getBindingUri(b) === uri)) {
            this._vocabularyBindings.push(binding);
        }
    }
    removeVocabularyBinding(uri) {
        const index = this._vocabularyBindings.findIndex(b => this.getBindingUri(b) === uri);
        if (index >= 0) {
            this._vocabularyBindings.splice(index, 1);
            return true;
        }
        return false;
    }
    getVocabularyBinding(uri) {
        return this._vocabularyBindings.find(b => this.getBindingUri(b) === uri);
    }
    hasVocabularyBinding(uri) {
        return this.getVocabularyBinding(uri) !== undefined;
    }
    /**
     * Serialize to JSON-LD with per-object @context.
     */
    toJSON() {
        const allVocabularies = this._vocabularyBindings.map(b => ({
            '@type': 'Vocabulary',
            '@id': this.getBindingUri(b),
            label: b.name,
        }));
        return {
            '@context': {
                '@vocab': 'http://semantical.io/ontology/core#',
                includesVocabulary: { '@type': '@id', '@container': '@set' }
            },
            '@type': 'UbiquitousLanguage',
            includesVocabulary: allVocabularies
        };
    }
    /**
     * Deserialize from JSON-LD or legacy format.
     * Requires a factory function to create VocabularyBinding instances from config data.
     */
    static fromJSON(json, bindingFactory) {
        const ul = new UbiquitousLanguageImpl({
            id: json.id ?? 'ubiquitous-language',
            name: json.name ?? 'Ubiquitous Language'
        });
        if (!bindingFactory) {
            // Without a factory, we can't create VocabularyBinding instances
            return ul;
        }
        // Handle JSON-LD format (includesVocabulary)
        if (json.includesVocabulary) {
            for (const v of json.includesVocabulary) {
                ul.addVocabularyBinding(bindingFactory({
                    id: v['@id'],
                    name: v.label,
                    namespace: v['@id'],
                    vocabularyIri: v['@id'],
                }));
            }
        }
        // Handle legacy format (importedVocabularies/localVocabularies)
        if (json.importedVocabularies) {
            for (const v of json.importedVocabularies) {
                ul.addVocabularyBinding(bindingFactory({
                    id: v.uri,
                    name: v.label,
                    namespace: v.uri,
                    vocabularyIri: v.uri,
                }));
            }
        }
        if (json.localVocabularies) {
            for (const v of json.localVocabularies) {
                ul.addVocabularyBinding(bindingFactory({
                    id: v.uri,
                    name: v.label,
                    namespace: v.uri,
                    vocabularyIri: v.uri,
                }));
            }
        }
        return ul;
    }
}
